import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { BrandProvider } from './contexts/BrandContext';
import { ToastProvider } from './components/Toast';
import { ConfirmProvider } from './components/ConfirmProvider';
import Layout from './components/Layout';
import { ruleFor, homePath, allowRule, ROUTE_MODULES } from './utils/access';
import { loginPath } from './utils/authMode';
import { useBrand } from './contexts/BrandContext';
import { useSetupStatus } from './hooks/useSetupStatus';

// CODE_SPLITTING — все страницы лениво загружаются (React.lazy + Suspense).
// Это разбивает один main.js (~1.7 МБ) на отдельные chunks по странице.
// Первая загрузка приложения становится в разы быстрее, особенно на медленных
// сетях/мобилках. Login страница загружается eagerly чтобы редирект был мгновенным.
import LoginPage from './pages/LoginPage';

// LAZY_RETRY — при сбое загрузки chunk'а (ChunkLoadError) почти всегда виноват
// свежий деплой фронта: закэшированный у клиента index ссылается на изменившийся
// хэш chunk'а, файл не находится, и страница/карточка молча не открывается. Такой
// был баг «в печати этикеток Lamoda пропал предпросмотр» — не грузился chunk
// страницы товара, из-за чего не рендерилась вся карточка с блоком этикетки.
// Один раз перезагружаем страницу, чтобы забрать актуальный бандл; guard в
// sessionStorage не даёт зациклиться, а сброс при успешной загрузке позволяет
// восстановиться и после следующего деплоя в рамках той же сессии.
function lazyRetry(factory) {
  return lazy(() =>
    factory()
      .then((mod) => { try { sessionStorage.removeItem('chunk_reloaded'); } catch (e) {} return mod; })
      .catch((err) => {
        try {
          if (!sessionStorage.getItem('chunk_reloaded')) {
            sessionStorage.setItem('chunk_reloaded', String(Date.now()));
            window.location.reload();
            return new Promise(() => {}); // висим до перезагрузки
          }
        } catch (e) {}
        throw err; // уже перезагружались — не зацикливаемся, отдаём ошибку в ErrorBoundary/Suspense
      })
  );
}

const CompaniesPage = lazyRetry(() => import('./pages/CompaniesPage'));
const CompanyDetailPage = lazyRetry(() => import('./pages/CompanyDetailPage'));
const OrdersPage = lazyRetry(() => import('./pages/OrdersPage'));
const CreateOrderPage = lazyRetry(() => import('./pages/CreateOrderPage'));
const BarcodesPage = lazyRetry(() => import('./pages/BarcodesPage'));
const ProcessingListPage = lazyRetry(() => import('./pages/ProcessingListPage'));
const ProcessingDetailPage = lazyRetry(() => import('./pages/ProcessingDetailPage'));
const FbsPage = lazyRetry(() => import('./pages/FbsPage'));
const FbsHubPage = lazyRetry(() => import('./pages/FbsHubPage'));
const OzonFbsPage = lazyRetry(() => import('./pages/OzonFbsPage'));
const FbsDashboardPage = lazyRetry(() => import('./pages/FbsDashboardPage'));
const SupplyDetailPage = lazyRetry(() => import('./pages/SupplyDetailPage'));
const DashboardPage = lazyRetry(() => import('./pages/DashboardPage'));
const UsersPage = lazyRetry(() => import('./pages/UsersPage'));
const StaffAccessPage = lazyRetry(() => import('./pages/StaffAccessPage'));
const BadgeLoginPage = lazyRetry(() => import('./pages/BadgeLoginPage'));
const JobSignupPage = lazyRetry(() => import('./pages/JobSignupPage'));
const TempWorkersPage = lazyRetry(() => import('./pages/TempWorkersPage'));
const ShiftBoardPage = lazyRetry(() => import('./pages/ShiftBoardPage'));
const WarehousePage = lazyRetry(() => import('./pages/WarehousePage'));
const ArrivalsPage = lazyRetry(() => import('./pages/ArrivalsPage'));
const PickupsPage = lazyRetry(() => import('./pages/PickupsPage'));
const MarkingPage = lazyRetry(() => import('./pages/MarkingPage'));
const PickingPage = lazyRetry(() => import('./pages/PickingPage'));
const ScanningPage = lazyRetry(() => import('./pages/ScanningPage'));
const ReprintPage = lazyRetry(() => import('./pages/ReprintPage'));
const FbsAssemblyPage = lazyRetry(() => import('./pages/FbsAssemblyPage'));
const FbsWhereIsPage = lazyRetry(() => import('./pages/FbsWhereIsPage'));
const ActivityMonitorPage = lazyRetry(() => import('./pages/ActivityMonitorPage'));
const TsdPage = lazyRetry(() => import('./pages/TsdPage'));
const WorkPage = lazyRetry(() => import('./pages/WorkPage'));
const BoardPage = lazyRetry(() => import('./pages/BoardPage'));
const TasksPage = lazyRetry(() => import('./pages/TasksPage'));
const CellAuditPage = lazyRetry(() => import('./pages/CellAuditPage'));
const FbsZoneIntakePage = lazyRetry(() => import('./pages/FbsZoneIntakePage'));
const FbsRecountPage = lazyRetry(() => import('./pages/FbsRecountPage'));
const FbsTailsPage = lazyRetry(() => import('./pages/FbsTailsPage'));
const PalletAuditConfirmPage = lazyRetry(() => import('./pages/PalletAuditConfirmPage'));
const ServicesPage = lazyRetry(() => import('./pages/ServicesPage'));
const AiSettingsPage = lazyRetry(() => import('./pages/AiSettingsPage'));
const BrandSettingsPage = lazyRetry(() => import('./pages/BrandSettingsPage'));
const RequisitesPage = lazyRetry(() => import('./pages/RequisitesPage'));
// Облако Н3: заявка на склад, установка пароля по ссылке, документы, мастер первого запуска
const SignupPage = lazyRetry(() => import('./pages/SignupPage'));
const SetPasswordPage = lazyRetry(() => import('./pages/SetPasswordPage'));
// Облако 28.09: обязательная смена временного пароля — до мастера и меню
const ChangeTempPasswordPage = lazyRetry(() => import('./pages/ChangeTempPasswordPage'));
const LegalPage = lazyRetry(() => import('./pages/LegalPage'));
const SetupWizardPage = lazyRetry(() => import('./pages/SetupWizardPage'));
const StartPage = lazyRetry(() => import('./pages/StartPage'));
const ModulesCatalogPage = lazyRetry(() => import('./pages/ModulesCatalogPage'));
const SubscriptionPage = lazyRetry(() => import('./pages/SubscriptionPage'));
const SupportLoginPage = lazyRetry(() => import('./pages/SupportLoginPage'));
// Админка платформы — отдельный сайт platform.<домен> (28.09), своё приложение и вход
const PlatformConsole = lazyRetry(() => import('./platform-console/PlatformConsole'));
const isPlatformConsoleHost = () => /^platform\./.test(window.location.hostname)
  && !/^\/(signup|set-password|legal)/.test(window.location.pathname);
const ProductServicesTzPage = lazyRetry(() => import('./pages/ProductServicesTzPage'));
const ProductsPage = lazyRetry(() => import('./pages/ProductsPage'));
const TrucksPage = lazyRetry(() => import('./pages/TrucksPage'));
const MarketplacePage = lazyRetry(() => import('./pages/MarketplacePage'));
const AnalyticsPage = lazyRetry(() => import('./pages/AnalyticsPage'));
const WarehouseActivityPage = lazyRetry(() => import('./pages/WarehouseActivityPage'));
const StockLogPage = lazyRetry(() => import('./pages/StockLogPage'));
const StockTransferPage = lazyRetry(() => import('./pages/StockTransferPage'));
const ArrivalPage = lazyRetry(() => import('./pages/ArrivalPage'));
const ArrivalRedirectLazy = lazyRetry(() => import('./pages/ArrivalPage').then((m) => ({ default: m.ArrivalRedirect })));
const ProductManagementPage = lazyRetry(() => import('./pages/ProductManagementPage'));
const StoragePage = lazyRetry(() => import('./pages/StoragePage'));
const WarehouseBuilderPage = lazyRetry(() => import('./pages/WarehouseBuilderPage'));
const PlacementPage = lazyRetry(() => import('./pages/PlacementPage'));
const BoxPlacementPage = lazyRetry(() => import('./pages/BoxPlacementPage'));
const FbsMovePage = lazyRetry(() => import('./pages/FbsMovePage'));
const FbsZoneControlPage = lazyRetry(() => import('./pages/FbsZoneControlPage'));
const FbsReturnBufferPage = lazyRetry(() => import('./pages/FbsReturnBufferPage'));
const FbsUnacceptedPage = lazyRetry(() => import('./pages/FbsUnacceptedPage'));
const FbsCheckBatchPage = lazyRetry(() => import('./pages/FbsCheckBatchPage'));
const KizTracePage = lazyRetry(() => import('./pages/KizTracePage'));
const ZoneLedgerPage = lazyRetry(() => import('./pages/ZoneLedgerPage'));
const BoxRecountPage = lazyRetry(() => import('./pages/BoxRecountPage'));
const PrintersPage = lazyRetry(() => import('./pages/PrintersPage'));
const ShippingWarehousesPage = lazyRetry(() => import('./pages/ShippingWarehousesPage'));
const LabelTemplatesPage = lazyRetry(() => import('./pages/LabelTemplatesPage'));
const StorageBillingPage = lazyRetry(() => import('./pages/StorageBillingPage'));
const FinancePage = lazyRetry(() => import('./pages/FinancePage'));
const ConsumablesPage = lazyRetry(() => import('./pages/ConsumablesPage'));
const CalculatorPage = lazyRetry(() => import('./pages/CalculatorPage'));
const CalculationPage = lazyRetry(() => import('./pages/CalculationPage'));
const FbsCalculatorPage = lazyRetry(() => import('./pages/FbsCalculatorPage'));
const ClientOnboardingPage = lazyRetry(() => import('./pages/ClientOnboardingPage'));
const WhatsNewPage = lazyRetry(() => import('./pages/WhatsNewPage'));
const MyConsumablesPage = lazyRetry(() => import('./pages/MyConsumablesPage'));
const AdminFinancePage = lazyRetry(() => import('./pages/AdminFinancePage'));
const ApiKeyPage = lazyRetry(() => import('./pages/ApiKeyPage'));
const KnowledgePage = lazyRetry(() => import('./pages/KnowledgePage'));
const KnowledgeCategoryPage = lazyRetry(() => import('./pages/KnowledgeCategoryPage'));
const KnowledgeArticlePage = lazyRetry(() => import('./pages/KnowledgeArticlePage'));
const AdminKnowledgePage = lazyRetry(() => import('./pages/AdminKnowledgePage'));
const UiKitPage = lazyRetry(() => import('./pages/UiKitPage'));
const ProfilePage = lazyRetry(() => import('./pages/ProfilePage'));
const DefectsPage = lazyRetry(() => import('./pages/DefectsPage'));
const ClientBrakPage = lazyRetry(() => import('./pages/ClientBrakPage'));
const AdminNotificationsPage = lazyRetry(() => import('./pages/AdminNotificationsPage'));
const PvzReturnsListPage = lazyRetry(() => import('./pages/PvzReturnsListPage'));
const PvzReturnSessionPage = lazyRetry(() => import('./pages/PvzReturnSessionPage'));
const OwnerFinancePage = lazyRetry(() => import('./pages/OwnerFinancePage'));
const MoneyPage = lazyRetry(() => import('./pages/MoneyPage'));
const CrmDealsPage = lazyRetry(() => import('./pages/CrmDealsPage'));
const CrmDealPage = lazyRetry(() => import('./pages/CrmDealPage'));
const CrmLeadsPage = lazyRetry(() => import('./pages/CrmLeadsPage'));
const CrmLeadPage = lazyRetry(() => import('./pages/CrmLeadPage'));
const CrmContactsPage = lazyRetry(() => import('./pages/CrmContactsPage'));
const CrmContactPage = lazyRetry(() => import('./pages/CrmContactPage'));
const CrmOrganizationsPage = lazyRetry(() => import('./pages/CrmOrganizationsPage'));
const CrmOrganizationPage = lazyRetry(() => import('./pages/CrmOrganizationPage'));
const CrmReportsPage = lazyRetry(() => import('./pages/CrmReportsPage'));
const MarketingPage = lazyRetry(() => import('./pages/MarketingPage'));
const CrmHomePage = lazyRetry(() => import('./pages/CrmHomePage'));
const CrmOverviewPage = lazyRetry(() => import('./pages/CrmOverviewPage'));
const CrmTasksPage = lazyRetry(() => import('./pages/CrmTasksPage'));
const CrmSettingsPage = lazyRetry(() => import('./pages/CrmSettingsPage'));
const CrmAuditPage = lazyRetry(() => import('./pages/CrmAuditPage'));
const CrmTrashPage = lazyRetry(() => import('./pages/CrmTrashPage'));
// Облако Н3: кабинет платформы (только владелец Full-Fix)
const PlatformAdminPage = lazyRetry(() => import('./pages/PlatformAdminPage'));

// PageLoader — fallback для Suspense пока chunk качается
function PageLoader() {
  return (
    <div style={{ padding: '2rem', textAlign: 'center' }}>
      <span className="spinner" />
    </div>
  );
}

/**
 * Куда попадает человек после входа, с «/» и с закрытого адреса.
 *
 * Ждём загрузку профиля: без этого роль ещё не известна, и администратора уносило
 * на складской рабочий стол вместо обработки заказов. Сам адрес считает
 * homePath (utils/access.js): клиенту — дашборд, администратору — обработка,
 * кладовщику — «Мои задачи», если есть право, иначе первый открытый ему раздел,
 * иначе профиль. Он всегда открывается, поэтому редиректа на себя не бывает.
 */
function HomeRedirect() {
  const auth = useAuth();
  const { brand } = useBrand();
  // Облако: администратору нового склада — мастер (режим фокуса) или «С чего начать»,
  // пока на складе нет ни приёмок, ни отгрузок. Full-Fix статус мастера не спрашивает.
  const cloudAdmin = !!auth.user && !brand.default && auth.isAdmin && !auth.user.company_id;
  const { st, loading } = useSetupStatus(cloudAdmin);
  if (auth.loading) return <PageLoader />;
  if (!auth.user) return <Navigate to={loginPath()} replace />;
  if (cloudAdmin) {
    if (loading && !st) return <PageLoader />;
    if (st && st.enabled && st.focus) return <Navigate to="/setup" replace />;
    if (st && st.enabled && (st.fresh || !st.done)) return <Navigate to="/start" replace />;
  }
  return <Navigate to={homePath(auth, { cloud: !brand.default })} replace />;
}

/**
 * Guard — единая обёртка маршрута вместо ProtectedRoute / AdminRoute /
 * CompanyAdminRoute / PermissionRoute / FinanceRoute / InternalRoute /
 * InternalSectionRoute. Правило берётся из той же таблицы (utils/access.js),
 * по которой строятся меню и плитки, — пункт меню не может вести туда, куда
 * маршрут не пустит.
 *
 * Раньше закрытый адрес уводил на /processing. У кладовщика без раздела
 * 'processing' тот уводил сам на себя — белый экран без меню и выхода (аудит
 * UI 25.09). Теперь — на домашний экран роли с плашкой «Нет доступа к разделу».
 */
/**
 * Старые адреса «Вернуть из ячеек отгрузки» (/otg-return) и «Сверка палеты»
 * (/pallet-audit, ?mode=stale, ?cell=…) ведут в объединённый экран /cell-audit
 * на нужную вкладку: закладки и ссылки из сообщений бота продолжают работать.
 */
function LegacyCellAudit({ tab, kind }) {
  const location = useLocation();
  const old = new URLSearchParams(location.search);
  const next = new URLSearchParams();
  next.set('tab', old.get('mode') === 'stale' ? 'cells' : tab);
  if (kind) next.set('kind', kind);
  if (old.get('cell')) next.set('cell', old.get('cell'));
  return <Navigate to={`/cell-audit?${next.toString()}`} replace />;
}

function Guard({ path, children }) {
  const auth = useAuth();
  const location = useLocation();
  if (auth.loading) return <PageLoader />;
  if (!auth.user) return <Navigate to={loginPath()} replace />;
  const rule = ruleFor(path);
  // Облако: тариф ещё грузится, а раздел зависит от модуля — ждём, а не уводим
  if (auth.planPending && ROUTE_MODULES[path]) return <PageLoader />;
  if (allowRule(rule, auth)) return children;
  // Кабинет платформы открывают по прямой ссылке: право приходит с тарифом
  // (/api/platform/plan) — дождаться его, а не уводить на домашний экран
  if (path === '/platform-admin' && !auth.plan) return <PageLoader />;
  return (
    <Navigate
      to={homePath(auth)}
      replace
      state={{
        denied: rule?.label || location.pathname,
        // Право есть, а модуль выключен (тариф или «Каталог модулей») — другой текст:
        // руководителю незачем «просить руководителя» (29.09, проход облака)
        deniedModule: !!(rule && rule.allow(auth)),
      }}
    />
  );
}

function AppRoutes() {
  const auth = useAuth();
  const { user } = auth;

  // Облако: вошёл с временным паролем, выданным системой, — сначала свой пароль.
  // Сервер до смены отвечает 403 на всё, кроме профиля и смены пароля; у Full-Fix
  // флаг не приходит, ветка не срабатывает.
  if (user && user.must_change_password) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="*" element={<ChangeTempPasswordPage />} />
        </Routes>
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Вход: уже вошедшего — на его домашний экран (раньше всех складских
            уносило на /processing, куда у кладовщика может не быть права) */}
        <Route path="/login" element={user ? <Navigate to={homePath(auth)} replace /> : <LoginPage />} />
        {/* Терминал склада: вход сканом бейджа. Публичный, как и обычный логин. */}
        <Route path="/badge" element={<BadgeLoginPage />} />
        {/* Запись на подработку с сайта (QR на стене) — публичная, без входа (02.10) */}
        <Route path="/job" element={<JobSignupPage />} />
        {/* Облако Н3 — публичные: заявка на склад (хост платформы; у Full-Fix «скоро»),
            установка пароля по одноразовой ссылке, тексты документов (заглушки, TODO юристу) */}
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/set-password" element={<SetPasswordPage />} />
        <Route path="/support-login" element={<SupportLoginPage />} />
        <Route path="/legal/offer" element={<LegalPage doc="offer" />} />
        <Route path="/legal/privacy" element={<LegalPage doc="privacy" />} />
        {/* Куда попадает человек после входа: клиент — на дашборд, сотрудник склада —
            на свои задачи, администратор — в обработку заказов, как раньше */}
        <Route path="/" element={<HomeRedirect />} />
        <Route path="/companies" element={<Guard path="/companies"><Layout><CompaniesPage /></Layout></Guard>} />
        <Route path="/companies/:id" element={<Guard path="/companies/:id"><Layout><CompanyDetailPage /></Layout></Guard>} />
        <Route path="/orders" element={<Guard path="/orders"><Layout><OrdersPage /></Layout></Guard>} />
        <Route path="/orders/create" element={<Guard path="/orders/create"><Layout><CreateOrderPage /></Layout></Guard>} />
        <Route path="/barcodes" element={<Guard path="/barcodes"><Layout><BarcodesPage /></Layout></Guard>} />
        <Route path="/calculator" element={<Guard path="/calculator"><Layout><CalculatorPage /></Layout></Guard>} />
        {/* Расчёт стоимости — коммерческое КП. Секция прав «calculation», клиентам недоступно */}
        <Route path="/calculation" element={<Guard path="/calculation"><Layout><CalculationPage /></Layout></Guard>} />
        <Route path="/fbs-calculator" element={<Guard path="/fbs-calculator"><Layout><FbsCalculatorPage /></Layout></Guard>} />
        {/* Квиз «Настроим кабинет» клиента фулфилмента (08.10) */}
        <Route path="/onboarding" element={<Guard path="/onboarding"><ClientOnboardingPage /></Guard>} />
        {/* «Что нового» (08.10): обновления платформы — каждому своё, отбор на сервере */}
        <Route path="/whats-new" element={<Guard path="/whats-new"><Layout><WhatsNewPage /></Layout></Guard>} />
        <Route path="/processing" element={<Guard path="/processing"><Layout><ProcessingListPage /></Layout></Guard>} />
        <Route path="/processing/:id" element={<Guard path="/processing/:id"><Layout><ProcessingDetailPage /></Layout></Guard>} />
        <Route path="/temp-workers" element={<Guard path="/temp-workers"><Layout><TempWorkersPage /></Layout></Guard>} />
        <Route path="/fbs" element={<Guard path="/fbs"><Layout><FbsPage /></Layout></Guard>} />
        <Route path="/fbs-hub" element={<Guard path="/fbs-hub"><Layout><FbsHubPage /></Layout></Guard>} />
        <Route path="/ozon-fbs" element={<Guard path="/ozon-fbs"><Layout><OzonFbsPage /></Layout></Guard>} />
        <Route path="/fbs-dashboard" element={<Guard path="/fbs-dashboard"><Layout><FbsDashboardPage /></Layout></Guard>} />
        <Route path="/trucks" element={<Guard path="/trucks"><Layout><TrucksPage /></Layout></Guard>} />
        <Route path="/processing/supply/:supplyId" element={<Guard path="/processing/supply/:supplyId"><Layout><SupplyDetailPage /></Layout></Guard>} />
        <Route path="/dashboard" element={<Guard path="/dashboard"><Layout><DashboardPage /></Layout></Guard>} />
        <Route path="/users" element={<Guard path="/users"><Layout><UsersPage /></Layout></Guard>} />
        <Route path="/staff-access" element={<Guard path="/staff-access"><Layout><StaffAccessPage /></Layout></Guard>} />
        <Route path="/shift" element={<Guard path="/shift"><Layout><ShiftBoardPage /></Layout></Guard>} />
        <Route path="/warehouse" element={<Guard path="/warehouse"><Layout><WarehousePage /></Layout></Guard>} />
        <Route path="/arrivals" element={<Guard path="/arrivals"><Layout><ArrivalsPage /></Layout></Guard>} />
        <Route path="/pickups" element={<Guard path="/pickups"><Layout><PickupsPage /></Layout></Guard>} />
        <Route path="/pvz-returns" element={<Guard path="/pvz-returns"><Layout><PvzReturnsListPage /></Layout></Guard>} />
        <Route path="/pvz-returns/:id" element={<Guard path="/pvz-returns/:id"><Layout><PvzReturnSessionPage /></Layout></Guard>} />
        {/* «Данные системы» убраны (решение владельца 27.09): восемь счётчиков без
            продуктовой ценности. Старые адреса ведут на домашний экран роли. */}
        <Route path="/system-data" element={<HomeRedirect />} />
        <Route path="/import" element={<HomeRedirect />} />
        <Route path="/marking" element={<Guard path="/marking"><Layout><MarkingPage /></Layout></Guard>} />
        <Route path="/picking" element={<Guard path="/picking"><Layout><PickingPage /></Layout></Guard>} />
        <Route path="/scanning" element={<Guard path="/scanning"><Layout><ScanningPage /></Layout></Guard>} />
        <Route path="/reprint" element={<Guard path="/reprint"><Layout><ReprintPage /></Layout></Guard>} />
        <Route path="/fbs-assembly" element={<Guard path="/fbs-assembly"><Layout><FbsAssemblyPage /></Layout></Guard>} />
        <Route path="/where-is" element={<Guard path="/where-is"><Layout><FbsWhereIsPage /></Layout></Guard>} />
        <Route path="/monitor" element={<Guard path="/monitor"><Layout><ActivityMonitorPage /></Layout></Guard>} />
        <Route path="/services" element={<Guard path="/services"><Layout><ServicesPage /></Layout></Guard>} />
        <Route path="/admin/ai" element={<Guard path="/admin/ai"><Layout><AiSettingsPage /></Layout></Guard>} />
        {/* Облако (28.09): бренд и свой домен склада, реквизиты исполнителя для документов */}
        <Route path="/settings/brand" element={<Guard path="/settings/brand"><Layout><BrandSettingsPage /></Layout></Guard>} />
        <Route path="/settings/requisites" element={<Guard path="/settings/requisites"><Layout><RequisitesPage /></Layout></Guard>} />
        {/* Мастер — свой полноэкранный каркас без меню (режим фокуса первого запуска);
            у Full-Fix он сам оборачивается в Layout. «С чего начать» — главная нового склада */}
        <Route path="/setup" element={<Guard path="/setup"><SetupWizardPage /></Guard>} />
        <Route path="/start" element={<Guard path="/start"><Layout><StartPage /></Layout></Guard>} />
        <Route path="/modules" element={<Guard path="/modules"><Layout><ModulesCatalogPage /></Layout></Guard>} />
        <Route path="/subscription" element={<Guard path="/subscription"><Layout><SubscriptionPage /></Layout></Guard>} />
        <Route path="/product-services-tz" element={<Guard path="/product-services-tz"><Layout><ProductServicesTzPage /></Layout></Guard>} />
        <Route path="/products" element={<Guard path="/products"><Layout><ProductsPage /></Layout></Guard>} />
        <Route path="/products/:id" element={<Guard path="/products/:id"><Layout><ProductsPage /></Layout></Guard>} />
        <Route path="/marketplace" element={<Guard path="/marketplace"><Layout><MarketplacePage /></Layout></Guard>} />
        <Route path="/analytics" element={<Guard path="/analytics"><Layout><AnalyticsPage /></Layout></Guard>} />
        <Route path="/warehouse-activity" element={<Guard path="/warehouse-activity"><Layout><WarehouseActivityPage /></Layout></Guard>} />
        <Route path="/stock-log" element={<Guard path="/stock-log"><Layout><StockLogPage /></Layout></Guard>} />
        <Route path="/stock-transfer" element={<Guard path="/stock-transfer"><Layout><StockTransferPage /></Layout></Guard>} />
        <Route path="/product-management" element={<Guard path="/product-management"><Layout><ProductManagementPage /></Layout></Guard>} />
        {/* 28.09: одна приёмка — одна страница (карточка + «Пересчёт» + «По коробам»);
            прежние адреса пересчёта и коробов ведут туда */}
        <Route path="/arrivals/:id" element={<Guard path="/arrivals/:id"><Layout><ArrivalPage /></Layout></Guard>} />
        <Route path="/arrivals/:id/warehouse" element={<ArrivalRedirectLazy tab="recount" />} />
        <Route path="/arrivals/:id/boxes" element={<ArrivalRedirectLazy tab="boxes" />} />
        <Route path="/finances" element={<Guard path="/finances"><Layout><OwnerFinancePage /></Layout></Guard>} />
        <Route path="/money" element={<Guard path="/money"><Layout><MoneyPage /></Layout></Guard>} />
        {/* CRM full-fix — внутренний sales-модуль (секция 'crm') */}
        <Route path="/crm" element={<Guard path="/crm"><Layout><CrmDealsPage /></Layout></Guard>} />
        <Route path="/crm/home" element={<Guard path="/crm/home"><Layout><CrmHomePage /></Layout></Guard>} />
        <Route path="/crm/overview" element={<Guard path="/crm/overview"><Layout><CrmOverviewPage /></Layout></Guard>} />
        <Route path="/crm/deals/:id" element={<Guard path="/crm/deals/:id"><Layout><CrmDealPage /></Layout></Guard>} />
        <Route path="/crm/leads" element={<Guard path="/crm/leads"><Layout><CrmLeadsPage /></Layout></Guard>} />
        <Route path="/crm/leads/:id" element={<Guard path="/crm/leads/:id"><Layout><CrmLeadPage /></Layout></Guard>} />
        <Route path="/crm/tasks" element={<Guard path="/crm/tasks"><Layout><CrmTasksPage /></Layout></Guard>} />
        <Route path="/crm/contacts" element={<Guard path="/crm/contacts"><Layout><CrmContactsPage /></Layout></Guard>} />
        <Route path="/crm/contacts/:id" element={<Guard path="/crm/contacts/:id"><Layout><CrmContactPage /></Layout></Guard>} />
        <Route path="/crm/organizations" element={<Guard path="/crm/organizations"><Layout><CrmOrganizationsPage /></Layout></Guard>} />
        <Route path="/crm/organizations/:id" element={<Guard path="/crm/organizations/:id"><Layout><CrmOrganizationPage /></Layout></Guard>} />
        <Route path="/crm/reports" element={<Guard path="/crm/reports"><Layout><CrmReportsPage /></Layout></Guard>} />
        <Route path="/marketing" element={<Guard path="/marketing"><Layout><MarketingPage /></Layout></Guard>} />
        <Route path="/crm/audit" element={<Guard path="/crm/audit"><Layout><CrmAuditPage /></Layout></Guard>} />
        <Route path="/crm/trash" element={<Guard path="/crm/trash"><Layout><CrmTrashPage /></Layout></Guard>} />
        <Route path="/crm/settings" element={<Guard path="/crm/settings"><Layout><CrmSettingsPage /></Layout></Guard>} />
        {/* Старый /payroll влит в финмодуль — оставляем доступ для совместимости */}
        <Route path="/payroll" element={<Guard path="/payroll"><Layout><OwnerFinancePage /></Layout></Guard>} />
        <Route path="/storage" element={<Guard path="/storage"><Layout><StoragePage /></Layout></Guard>} />
        <Route path="/warehouse-builder" element={<Guard path="/warehouse-builder"><Layout><WarehouseBuilderPage /></Layout></Guard>} />
        <Route path="/placement" element={<Guard path="/placement"><Layout><PlacementPage /></Layout></Guard>} />
        <Route path="/box-placement" element={<Guard path="/box-placement"><Layout><BoxPlacementPage /></Layout></Guard>} />
        <Route path="/fbs-move" element={<Guard path="/fbs-move"><Layout><FbsMovePage /></Layout></Guard>} />
        <Route path="/fbs-zone-control" element={<Guard path="/fbs-zone-control"><Layout><FbsZoneControlPage /></Layout></Guard>} />
        <Route path="/fbs-return-buffer" element={<Guard path="/fbs-return-buffer"><Layout><FbsReturnBufferPage /></Layout></Guard>} />
        {/* «Вернуть из ячеек отгрузки» — теперь вкладка объединённого экрана /cell-audit */}
        <Route path="/otg-return" element={<Guard path="/otg-return"><LegacyCellAudit tab="cells" kind="OTG" /></Guard>} />
        <Route path="/fbs-unaccepted" element={<Guard path="/fbs-unaccepted"><Layout><FbsUnacceptedPage /></Layout></Guard>} />
        <Route path="/fbs-check/:batch" element={<Guard path="/fbs-check/:batch"><Layout><FbsCheckBatchPage /></Layout></Guard>} />
        <Route path="/box-recount" element={<Guard path="/box-recount"><Layout><BoxRecountPage /></Layout></Guard>} />
        {/* Центр управления складом: весь процесс на одной доске */}
        {/* Сверка палеты в зоне ФБС: перекличка коробов и сверка содержимого */}
        {/* Подтверждение сверок: списание проводит только руководитель */}
        <Route path="/kiz-trace" element={<Guard path="/kiz-trace"><Layout><KizTracePage /></Layout></Guard>} />
        <Route path="/zone-ledger" element={<Guard path="/zone-ledger"><Layout><ZoneLedgerPage /></Layout></Guard>} />
        <Route path="/pallet-audit-confirm" element={<Guard path="/pallet-audit-confirm"><Layout><PalletAuditConfirmPage /></Layout></Guard>} />
        {/* Сверка палеты и ячейки закрытых документов — один экран (решение владельца 25.09) */}
        <Route path="/cell-audit" element={<Guard path="/cell-audit"><Layout><CellAuditPage /></Layout></Guard>} />
        <Route path="/pallet-audit" element={<Guard path="/pallet-audit"><LegacyCellAudit tab="pallet" /></Guard>} />
        <Route path="/fbs-zone-intake" element={<Guard path="/fbs-zone-intake"><Layout><FbsZoneIntakePage /></Layout></Guard>} />
        <Route path="/fbs-recount" element={<Guard path="/fbs-recount"><Layout><FbsRecountPage /></Layout></Guard>} />
        <Route path="/fbs-tails" element={<Guard path="/fbs-tails"><Layout><FbsTailsPage /></Layout></Guard>} />
        <Route path="/board" element={<Guard path="/board"><Layout><BoardPage /></Layout></Guard>} />
        {/* Рабочий стол склада — стартовый экран сотрудника */}
        <Route path="/work" element={<Guard path="/work"><Layout><WorkPage /></Layout></Guard>} />
        {/* Задачи склада (27.10): поручения и задачи клиентов; /tasks/:id — карточка поверх доски */}
        <Route path="/tasks" element={<Guard path="/tasks"><Layout><TasksPage /></Layout></Guard>} />
        <Route path="/tasks/:id" element={<Guard path="/tasks/:id"><Layout><TasksPage /></Layout></Guard>} />
        {/* Терминалы сбора данных: приложение и инструкция для склада */}
        <Route path="/tsd" element={<Guard path="/tsd"><Layout><TsdPage /></Layout></Guard>} />
        <Route path="/printers" element={<Guard path="/printers"><Layout><PrintersPage /></Layout></Guard>} />
        <Route path="/shipping-warehouses" element={<Guard path="/shipping-warehouses"><Layout><ShippingWarehousesPage /></Layout></Guard>} />
        <Route path="/shipments/create" element={<Navigate to="/orders/create" replace />} />
        <Route path="/label-templates" element={<Guard path="/label-templates"><Layout><LabelTemplatesPage /></Layout></Guard>} />
        <Route path="/defects" element={<Guard path="/defects"><Layout><DefectsPage /></Layout></Guard>} />
        <Route path="/client-brak" element={<Guard path="/client-brak"><Layout><ClientBrakPage /></Layout></Guard>} />
        <Route path="/storage-billing" element={<Guard path="/storage-billing"><Layout><StorageBillingPage /></Layout></Guard>} />
        <Route path="/finance" element={<Guard path="/finance"><Layout><FinancePage /></Layout></Guard>} />
        <Route path="/consumables" element={<Guard path="/consumables"><Layout><ConsumablesPage /></Layout></Guard>} />
        <Route path="/my-consumables" element={<Guard path="/my-consumables"><Layout><MyConsumablesPage /></Layout></Guard>} />
        <Route path="/admin/finance" element={<Guard path="/admin/finance"><Layout><AdminFinancePage /></Layout></Guard>} />
        <Route path="/integrations/api" element={<Guard path="/integrations/api"><Layout><ApiKeyPage /></Layout></Guard>} />
        <Route path="/knowledge/:category/:slug" element={<Guard path="/knowledge/:category/:slug"><Layout><KnowledgeArticlePage /></Layout></Guard>} />
        <Route path="/knowledge/:category" element={<Guard path="/knowledge/:category"><Layout><KnowledgeCategoryPage /></Layout></Guard>} />
        <Route path="/knowledge" element={<Guard path="/knowledge"><Layout><KnowledgePage /></Layout></Guard>} />
        <Route path="/admin/knowledge-base" element={<Guard path="/admin/knowledge-base"><Layout><AdminKnowledgePage /></Layout></Guard>} />
        <Route path="/profile" element={<Guard path="/profile"><Layout><ProfilePage /></Layout></Guard>} />
        <Route path="/admin/notifications" element={<Guard path="/admin/notifications"><Layout><AdminNotificationsPage /></Layout></Guard>} />
        <Route path="/platform-admin" element={<Guard path="/platform-admin"><Layout><PlatformAdminPage /></Layout></Guard>} />
        {/* Витрина дизайн-системы — только ADMIN, в меню нет. Старый адрес /ui-demo ведёт сюда. */}
        <Route path="/ui-kit" element={<Guard path="/ui-kit"><Layout><UiKitPage /></Layout></Guard>} />
        <Route path="/ui-demo" element={<Navigate to="/ui-kit" replace />} />
        {/* Неизвестный адрес — на домашний экран роли, а не на /processing */}
        <Route path="*" element={<HomeRedirect />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  if (isPlatformConsoleHost()) {
    return <Suspense fallback={<PageLoader />}><PlatformConsole /></Suspense>;
  }
  return (
    <AuthProvider>
      <ThemeProvider>
        <BrandProvider>
          <ToastProvider>
            <ConfirmProvider>
              <Router>
                <AppRoutes />
              </Router>
            </ConfirmProvider>
          </ToastProvider>
        </BrandProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
